import React, { useEffect, useMemo, useState } from 'react';
import useTypes from '@hooks/useTypes';
import { Checkbox, TableBody, TableCell, TableRow } from '@material-ui/core';
import { Select } from '@coolremote/shared';
import clsx from 'clsx';
import { t } from 'ttag';
import KeySelectTableRow from './KeySelectTableRow';
import { getParsedUniversalOptions } from './utils';


const UniversalMappingSettings = ({
  template,
  classes,
  onMappingChange,
  mappedObject,
}) => {

  const { universalEntityPropertiesByEntityType } = useTypes();
  const {property, propertyMapping = {}} = mappedObject;

  const [isEnabled, setIsEnabled] = useState(false);

  useEffect(() => {
    setIsEnabled(!!property);
  },[]);

  const type = 'controlUnit'; // currently only control unit points can be mapped as universal points, but this can be extended in the future.

  const { mainOptions } = useMemo(
    () =>
      getParsedUniversalOptions(
        template,
        universalEntityPropertiesByEntityType,
        mappedObject
      ),
    [template, universalEntityPropertiesByEntityType]
  );

  const selectOptions = useMemo(() => {
    const opts = mainOptions[type];
    if (!opts) return [];
    return opts.map(({ value, text, disabled }) => ({
      value,
      label: text,
      disabled,
    }));
  }, [mainOptions, type]);

  const handleCheckboxChange = ({ target: { checked } }) => {
    setIsEnabled(checked);
    if(!checked){
      onMappingChange({
        property: '',
        propertyMapping:{},
      });
    }
  };


  const handlePointChange = (value) => {
    onMappingChange({
      property: value,
      propertyMapping:{},
    });
  };

  const handlePointPropChange = (key) => (value) => {
    onMappingChange({
      propertyMapping: {
        ...propertyMapping,
        [key]: value,
      },
    });
  };

  const renderMainSelect = () => {
    if(!isEnabled){
      return null;
    }
    return (
      <Select
        value={mappedObject.property}
        onChange={handlePointChange}
        classes={{ trigger: classes.inputRoot }}
        options={selectOptions}
        menuPortal
        className={classes.width170}
      />
    );
  };

  /** This renders the options for enumerated types, with Select components with options defined by `mappedObject.customEnum` */
  const renderEnumeratedOptions = () => {

    const enums = universalEntityPropertiesByEntityType?.[type]?.[property]?.enum;

    if(!enums){
      return null;
    }

    return (
      <>
        {Object.values(enums).map((item) => (
          <KeySelectTableRow
            classes={classes}
            key={item.value}
            options={mappedObject.customEnum}
            value={propertyMapping?.[item.value]}
            onChange={handlePointPropChange(item.value)}
            label={item.text}
            selectedOptions={propertyMapping}
          />
        ))}
      </>
    );
  };

  /** This renders the open field (input) options for enumerated types */
  const renderOpenFieldOptions = () => {
    const enums = universalEntityPropertiesByEntityType?.[type]?.[property]?.enum;
    if(!enums){
      return null;
    }

    return (
      <>
        {Object.values(enums).map((item) => (
          <KeySelectTableRow
            classes={classes}
            key={item.value}
            value={propertyMapping?.[item.value]}
            onChange={handlePointPropChange(item.value)}
            label={item.text}
            selectedOptions={propertyMapping}
          />
        ))}
      </>
    );
  };

  const renderOptions = () => {
    if (!mappedObject.property) {
      return null;
    }

    const mappedPoint = universalEntityPropertiesByEntityType?.[type]?.[mappedObject.property];



    // if the mapped point is read only, we don't want to show any additional options.
    if(mappedPoint.readOnly){
      return null;
    }

    if(mappedObject.type === 5 && mappedPoint.type === 'number'){ //Analog Input object + number type
      return null; // currently no additional options for numerical points, but this can be extended in the future.
    }

    if(mappedPoint.type === 'enumerated' && mappedObject.type === 5){ //Analog Input object + enumerated type
      return renderOpenFieldOptions(); // Allow the user to set custom values for each enum option.
    }

    const hasEnum =  mappedObject.customEnum && Object.keys(mappedObject.customEnum).length > 0;

    if(mappedPoint.type === 'enumerated' && hasEnum){ // Any object + enumerated type with custom enum defined
      return renderEnumeratedOptions(); // Show the enum options in selects with predefined options based on the custom enum.
    }

    return null;


  };

  return (
    <TableBody className={classes.borderBottom}>
      <TableRow key={'row-universal-unit-point-enabled'}>
        <TableCell
          classes={{
            root: clsx(
              classes.tableCell,
              classes.tableCellBold,
              classes.checkboxContainer,
            ),
          }}
          align="left"
          style={{ width: '40%' }}
        >
          <Checkbox checked={isEnabled} onChange={handleCheckboxChange} />
          {t`Set as Universal Unit point`}
        </TableCell>
        <TableCell
          classes={{
            root: classes.tableCell,
          }}
          style={{ width: '60%' }}
        >
          {renderMainSelect()}
        </TableCell>
      </TableRow>
      {renderOptions()}
    </TableBody>
  );
};

export default UniversalMappingSettings;
